// Dismissible "verify your email" banner shown in the app shell while the
// current user's email is unverified. Soft prompt only — the app stays usable
// (REQUIRE_EMAIL_VERIFICATION is off during the tester phase). "Resend" asks the
// backend for a fresh link.
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "./auth-context";
import { resendVerification } from "@/api/auth";

export function VerifyEmailBanner() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [dismissed, setDismissed] = useState(false);
  const [state, setState] = useState<"idle" | "sending" | "sent">("idle");

  if (!user || user.email_verified || dismissed) return null;

  const onResend = async () => {
    setState("sending");
    try {
      await resendVerification();
      setState("sent");
    } catch {
      setState("idle");
    }
  };

  return (
    <div className="border-b border-amber-200 bg-amber-50 text-amber-900">
      <div className="container-page flex flex-wrap items-center gap-x-3 gap-y-1 py-2 text-sm">
        <span className="font-medium">{t("Please verify your email address.")}</span>
        <span className="text-amber-800/80">
          {t("We sent a link to {{email}}.", { email: user.email })}
        </span>
        <div className="ml-auto flex items-center gap-3">
          {state === "sent" ? (
            <span className="text-amber-800">{t("Link sent.")}</span>
          ) : (
            <button
              type="button"
              onClick={onResend}
              disabled={state === "sending"}
              className="font-semibold underline underline-offset-2 disabled:opacity-50"
            >
              {state === "sending" ? t("Sending…") : t("Resend")}
            </button>
          )}
          <button
            type="button"
            onClick={() => setDismissed(true)}
            aria-label={t("Dismiss")}
            className="text-amber-700/70 hover:text-amber-900"
          >
            ✕
          </button>
        </div>
      </div>
    </div>
  );
}
